import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  deleteDoc,
  getDocs,
  addDoc,
  serverTimestamp,
  orderBy,
  query
} from 'firebase/firestore';
import { db } from './firebase';
import type { Transaction, MonthData } from '@/types';

const getMonthsCollection = (userId: string, year: number) => 
  collection(db, `users/${userId}/years/${year}/months`);

const getTransactionsCollection = (userId: string, year: number, monthId: string) => 
  collection(db, `users/${userId}/years/${year}/months/${monthId}/transactions`);

export async function getMonthData(
  userId: string, 
  year: number, 
  monthId: string
): Promise<MonthData | null> {
  try {
    const monthDoc = doc(getMonthsCollection(userId, year), monthId);
    const monthSnap = await getDoc(monthDoc);

    if (monthSnap.exists()) {
      return monthSnap.data() as MonthData;
    }

    const newMonth: MonthData = {
      salary: 0,
      totalGains: 0,
      totalExpenses: 0,
      savings: 0,
      transactions: [],
    };

    await setDoc(monthDoc, newMonth);
    return newMonth;
  } catch (error) {
    console.error('Error getting month data:', error);
    return null;
  }
}

export async function updateMonthSalary(
  userId: string,
  year: number,
  monthId: string,
  salary: number
): Promise<void> {
  const monthDoc = doc(getMonthsCollection(userId, year), monthId);
  await updateDoc(monthDoc, { salary });
  await recalculateMonthTotals(userId, year, monthId);
}

export async function addTransaction(
  userId: string,
  year: number,
  monthId: string,
  transaction: Omit<Transaction, 'id' | 'createdAt'>
): Promise<string> {
  const transactionsCol = getTransactionsCollection(userId, year, monthId);

  // Build clean transaction — omit undefined values (e.g. empty notes)
  const cleanTransaction: Record<string, unknown> = {
    name: transaction.name,
    amount: transaction.amount,
    type: transaction.type,
    categoryId: transaction.categoryId,
    date: transaction.date,
    createdAt: serverTimestamp(),
  };

  if (transaction.notes && transaction.notes.trim()) {
    cleanTransaction.notes = transaction.notes.trim();
  }

  if (transaction.isRecurring) {
    cleanTransaction.isRecurring = true;
  }

  if (transaction.recurringTemplateId) {
    cleanTransaction.recurringTemplateId = transaction.recurringTemplateId;
  }

  const docRef = await addDoc(transactionsCol, cleanTransaction);
  await recalculateMonthTotals(userId, year, monthId);
  
  return docRef.id;
}

export async function deleteTransaction(
  userId: string,
  year: number,
  monthId: string,
  transactionId: string
): Promise<void> {
  const transactionDoc = doc(
    getTransactionsCollection(userId, year, monthId), 
    transactionId
  );
  
  await deleteDoc(transactionDoc);
  await recalculateMonthTotals(userId, year, monthId);
}

/**
 * Update a transaction
 */
export async function updateTransaction(
  userId: string,
  year: number,
  monthId: string,
  transactionId: string,
  updates: Partial<Omit<Transaction, 'id' | 'createdAt'>>
): Promise<void> {
  const transactionDoc = doc(
    getTransactionsCollection(userId, year, monthId),
    transactionId
  );

  // Remove undefined values from updates
  const cleanUpdates = Object.fromEntries(
    Object.entries(updates).filter(([, value]) => value !== undefined)
  );

  await updateDoc(transactionDoc, cleanUpdates);
  await recalculateMonthTotals(userId, year, monthId);
}

export async function getMonthTransactions(
  userId: string,
  year: number,
  monthId: string
): Promise<Transaction[]> {
  try {
    const transactionsCol = getTransactionsCollection(userId, year, monthId);
    const q = query(transactionsCol, orderBy('date', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      date: doc.data().date?.toDate?.() || new Date(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(),
    })) as Transaction[];
  } catch (error) {
    console.error('Error getting transactions:', error);
    return [];
  }
}

async function recalculateMonthTotals(
  userId: string,
  year: number,
  monthId: string
): Promise<void> {
  const transactions = await getMonthTransactions(userId, year, monthId);
  const monthDoc = doc(getMonthsCollection(userId, year), monthId);
  const monthSnap = await getDoc(monthDoc);
  const salary = monthSnap.exists() ? monthSnap.data().salary || 0 : 0;

  const totalGains = transactions
    .filter(t => t.type === 'gain')
    .reduce((sum, t) => sum + t.amount, 0);

  const totalExpenses = transactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + t.amount, 0);

  const savings = salary + totalGains - totalExpenses;

  // ✅ FIXED: Use setDoc with merge to create document if it doesn't exist
  await setDoc(monthDoc, {
    salary,
    totalGains,
    totalExpenses,
    savings,
  }, { merge: true });
}

export async function getYearSummary(userId: string, year: number) {
  try {
    const monthsCol = getMonthsCollection(userId, year);
    const snapshot = await getDocs(monthsCol);

    let totalSavings = 0;
    let totalIncome = 0;
    let totalExpenses = 0;

    snapshot.docs.forEach(doc => {
      const data = doc.data();
      totalSavings += data.savings || 0;
      totalIncome += (data.salary || 0) + (data.totalGains || 0);
      totalExpenses += data.totalExpenses || 0;
    });

    return { totalSavings, totalIncome, totalExpenses };
  } catch (error) {
    console.error('Error getting year summary:', error);
    return { totalSavings: 0, totalIncome: 0, totalExpenses: 0 };
  }
}

export async function getRecentTransactions(
  userId: string,
  year: number,
  limitCount: number = 10
): Promise<Transaction[]> {
  const monthsCol = getMonthsCollection(userId, year);
  const monthsSnapshot = await getDocs(monthsCol);
  
  const allTransactions: Transaction[] = [];
  
  for (const monthDoc of monthsSnapshot.docs) {
    const monthTransactions = await getMonthTransactions(
      userId, 
      year, 
      monthDoc.id
    );
    allTransactions.push(...monthTransactions);
  }
  
  return allTransactions
    .sort((a, b) => b.date.getTime() - a.date.getTime())
    .slice(0, limitCount);
}